import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { lazy, Suspense } from "react";

// Critical path — load immediately
import ComingSoon from "@/pages/ComingSoon";
import WaitlistPage from "@/pages/WaitlistPage";
const WaitlistAdmin = lazy(() => import("@/pages/WaitlistAdmin"));

// Full app pages — lazy loaded (locked behind Coming Soon during pre-launch)
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";

// Lazy-loaded — code-split for faster initial load
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Settings = lazy(() => import("@/pages/Settings"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Leaderboard = lazy(() => import("@/pages/Leaderboard"));
const PaymentCallback = lazy(() => import("@/pages/PaymentCallback"));
const ReceiptPage = lazy(() => import("@/pages/ReceiptPage"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const RewardsStore = lazy(() => import("@/pages/RewardsStore"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("@/pages/TermsConditions"));
const RefundPolicy = lazy(() => import("@/pages/RefundPolicy"));
const SecurityPolicy = lazy(() => import("@/pages/SecurityPolicy"));
const DataRetentionPolicy = lazy(() => import("@/pages/DataRetentionPolicy"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const PWAInstallPrompt = lazy(() => import("@/components/PWAInstallPrompt"));
const SupportChat = lazy(() => import("@/components/SupportChat"));

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center">
      <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
      <p className="text-primary font-mono text-xs tracking-widest">LOADING...</p>
    </div>
  </div>
);

// Support Chat wrapper — shows for all users except waitlist page
const GlobalSupportChat = () => {
  const { user } = useAuth();
  const location = useLocation();
  if (location.pathname === '/waitlist' || location.pathname === '/') return null;
  return <Suspense fallback={null}><SupportChat user={user} /></Suspense>;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center scanline-bg">
        <div className="text-center">
          <div className="w-16 h-16 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-primary font-mono text-sm tracking-widest">INITIALIZING SUB-ZERO...</p>
          <p className="text-muted-foreground text-xs mt-2">Establishing secure connection</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router with Auth Callback Detection
function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // PRE-LAUNCH LOCKDOWN: Only Coming Soon + Waitlist are public on custom domain
  // Emergent preview URL stays fully unlocked for testing
  const isCustomDomain = typeof window !== 'undefined' && !window.location.hostname.includes('emergentagent.com') && !window.location.hostname.includes('localhost');
  const isLocked = process.env.REACT_APP_LOCKDOWN !== 'false' && isCustomDomain;

  return (
    <Routes>
      <Route path="/" element={isLocked ? <ComingSoon /> : <Landing />} />
      <Route path="/waitlist" element={<WaitlistPage />} />
      <Route path="/waitlist-admin" element={<Suspense fallback={<PageLoader />}><WaitlistAdmin /></Suspense>} />
      {isLocked ? (
        <>
          {/* Legal pages stay accessible even in lockdown */}
          <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPolicy /></Suspense>} />
          <Route path="/terms" element={<Suspense fallback={<PageLoader />}><TermsConditions /></Suspense>} />
          <Route path="/refund" element={<Suspense fallback={<PageLoader />}><RefundPolicy /></Suspense>} />
          <Route path="/security-policy" element={<Suspense fallback={<PageLoader />}><SecurityPolicy /></Suspense>} />
          <Route path="/data-retention-policy" element={<Suspense fallback={<PageLoader />}><DataRetentionPolicy /></Suspense>} />
          {/* Everything else redirects to Coming Soon */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </>
      ) : (
        <>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/pricing" element={<Suspense fallback={<PageLoader />}><Pricing /></Suspense>} />
          <Route path="/dashboard" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Dashboard /></Suspense></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Settings /></Suspense></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Analytics /></Suspense></ProtectedRoute>} />
          <Route path="/leaderboard" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Leaderboard /></Suspense></ProtectedRoute>} />
          <Route path="/payment/callback" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><PaymentCallback /></Suspense></ProtectedRoute>} />
          <Route path="/receipts" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><ReceiptPage /></Suspense></ProtectedRoute>} />
          <Route path="/admin" element={<Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense>} />
          <Route path="/rewards" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><RewardsStore /></Suspense></ProtectedRoute>} />
          <Route path="/faq" element={<Suspense fallback={<PageLoader />}><FAQPage /></Suspense>} />
          <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPolicy /></Suspense>} />
          <Route path="/terms" element={<Suspense fallback={<PageLoader />}><TermsConditions /></Suspense>} />
          <Route path="/refund" element={<Suspense fallback={<PageLoader />}><RefundPolicy /></Suspense>} />
          <Route path="/security-policy" element={<Suspense fallback={<PageLoader />}><SecurityPolicy /></Suspense>} />
          <Route path="/data-retention-policy" element={<Suspense fallback={<PageLoader />}><DataRetentionPolicy /></Suspense>} />
          <Route path="/about" element={<Suspense fallback={<PageLoader />}><AboutPage /></Suspense>} />
          <Route path="/contact" element={<Suspense fallback={<PageLoader />}><ContactPage /></Suspense>} />
        </>
      )}
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App min-h-screen bg-background">
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Suspense fallback={null}><PWAInstallPrompt /></Suspense>
            <GlobalSupportChat />
            <Toaster 
              position="top-right"
              toastOptions={{
                style: {
                  background: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  color: 'hsl(var(--foreground))',
                },
              }}
            />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
